export function gradeFromScore(score: number): string {
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
}

export interface SnapshotRow {
  createdISO: string;
  score: number;
}
export interface SeriesPoint {
  dateISO: string;
  ts: number; // UTC ms of the point's dateISO (Date.UTC of YYYY-MM-DD) — the SAMPLE TARGET day (v3 grid)
  score: number;
}

// --- UTC-stable bucket keys (CRIT-1). Operate ONLY on the UTC YYYY-MM-DD substring. ---

// Count of days from a fixed epoch for a UTC calendar date (proleptic Gregorian).
// Pure integer math; no Date/timezone involved.
// Exported (D-decision): the rolling-window cutoff reuses it — DRY, no new date math.
export function utcDayNumber(y: number, m: number, d: number): number {
  // Date.UTC returns ms since 1970-01-01T00:00:00Z for the UTC calendar date — TZ-independent.
  return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
}

// UTC day number of an ISO string, read from its YYYY-MM-DD substring (TZ-independent).
// Exported (v3): ms math + pill decimation reuse it — DRY, no new date math.
export function utcDayOf(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return utcDayNumber(y, m, d);
}

// UTC ms at midnight of an ISO date's YYYY-MM-DD (TZ-independent).
export function utcMsOf(iso: string): number {
  return utcDayOf(iso) * 86_400_000;
}

// ISO of a UTC day number, formatted as a midnight-UTC YYYY-MM-DDT00:00:00Z string (same shape
// as SnapshotRow.createdISO). Reuses the day↔ms convention (no new date math) — `new Date(ms)` is a
// construction, not arithmetic; the slice keeps it UTC-stable so the emitted point sits on the grid.
function isoOfUtcDay(day: number): string {
  return `${new Date(day * 86_400_000).toISOString().slice(0, 10)}T00:00:00Z`;
}

// --- Fixed evenly-spaced sample grid + availability + default-window selection (§5). ---

export type WindowId = 'R1' | 'R3' | 'R12';
export interface WindowAvailability {
  ok: boolean;
  reason?: string;
}

// Min RENDERED points each window needs to be selectable (D-decision: gate on the
// rolling-window point counts, not raw series length). Tests reference this map.
export const WINDOW_THRESHOLDS = { R1: 2, R3: 3, R12: 4 } as const;

// Rolling span (UTC days) per window: R1=30d, R3=90d, R12=365d.
export const WINDOW_DAYS: Record<WindowId, number> = {
  R1: 30,
  R3: 90,
  R12: 365,
};

// Fixed point count per window (v3 anchored grid). The line spans EXACTLY [now − WINDOW_DAYS, now]
// with this many evenly-spaced targets, endpoints inclusive — so spacing is WINDOW_DAYS/(count−1):
//   R1  →  5 points  → 7.5 days apart
//   R3  → 10 points  → 10  days apart
//   R12 → 13 points  → ~30.4 days apart (≈ every 4 weeks — "evenly split, not too many")
export const WINDOW_POINTS: Record<WindowId, number> = {
  R1: 5,
  R3: 10,
  R12: 13,
};

export function computeAvailability(
  rows: SnapshotRow[],
  nowISO: string
): Record<WindowId, WindowAvailability> {
  // Gate on the count of POPULATED sampled points per window (D-decision): an unpopulated target
  // (tenant history shorter than the window) is omitted by sampleGrid, so it never counts.
  const count = (win: WindowId) => sampleGrid(rows, win, nowISO).length;
  return {
    R1:
      count('R1') >= WINDOW_THRESHOLDS.R1
        ? { ok: true }
        : { ok: false, reason: 'needs ~2 weeks of history' },
    R3:
      count('R3') >= WINDOW_THRESHOLDS.R3
        ? { ok: true }
        : { ok: false, reason: 'needs ~6 weeks of history' },
    R12:
      count('R12') >= WINDOW_THRESHOLDS.R12
        ? { ok: true }
        : { ok: false, reason: 'needs ~4 months of history' },
  };
}

// Default = window with the most history SPAN, among AVAILABLE windows (D3: span, not
// point count). Preference order R12 > R3 > R1 preserved from v1; manual selection overrides.
export function defaultWindow(
  a: Record<WindowId, WindowAvailability>
): WindowId | null {
  if (a.R12.ok) return 'R12';
  if (a.R3.ok) return 'R3';
  if (a.R1.ok) return 'R1';
  return null;
}

// Fixed evenly-spaced sample grid, BOTH ends anchored (v3 — replaces sliceWindow for rendering).
// Builds WINDOW_POINTS[win] target days evenly spanning EXACTLY [now − WINDOW_DAYS, now], endpoints
// inclusive, each intermediate target rounded to a whole UTC day for clean labels:
//   targetDay_k = utcDayOf(now) − round(k × WINDOW_DAYS/(count−1))   for k = count−1 … 0 (ascending out)
//   endpoints are exact: newest = utcDayOf(now), oldest = utcDayOf(now) − WINDOW_DAYS.
// Each target samples END-OF-PERIOD: the LATEST raw SnapshotRow whose UTC day ≤ targetDay (sampled
// from the RAW daily rows, NOT from weekly/monthly buckets). A point lands on the TARGET day's ts so
// it sits exactly on the grid. If NO snapshot exists ≤ targetDay (history shorter than the window),
// the target is OMITTED — the line then starts at the first populated sample, and the "Tracking
// since {date}" overlay + "Since {Mon D}" sublabel carry the partial-coverage story.
// `now` is injected (no Date.now() here); all math is UTC-only.
export function sampleGrid(
  rows: SnapshotRow[],
  win: WindowId,
  nowISO: string
): SeriesPoint[] {
  const nowDay = utcDayOf(nowISO);
  const span = WINDOW_DAYS[win];
  const count = WINDOW_POINTS[win];
  // Latest-first raw rows: lexical ISO order = UTC-correct (same basis as the old bucketLast).
  // For each target we want the latest row whose UTC day ≤ target, so descending lets us short-circuit.
  const sorted = rows
    .filter(r => r.createdISO !== '')
    .slice()
    .sort((a, b) => b.createdISO.localeCompare(a.createdISO));

  const out: SeriesPoint[] = [];
  for (let k = count - 1; k >= 0; k--) {
    const targetDay = nowDay - Math.round((k * span) / (count - 1));
    const hit = sorted.find(r => utcDayOf(r.createdISO) <= targetDay); // END-OF-PERIOD
    if (!hit) continue; // no snapshot ≤ target → omit (partial coverage)
    out.push({
      dateISO: isoOfUtcDay(targetDay), // ISO of the TARGET day (point sits on the grid)
      ts: targetDay * 86_400_000, // = utcMsOf(dateISO); the XAxis time scale plots this
      score: hit.score,
    });
  }
  return out; // ascending by target day (k counted down) — already sorted
}

// --- Time-axis pill decimation (v3) + sample-target ticks + coverage copy. ---

// Center-to-center pill pitch in px (pill ≈73 + 4 gap = 77), reused as the decimation min-gap.
const PITCH = 77;
// Min center spacing (px) between kept sample-target ticks.
const TICK_MIN_GAP = 70;

// Shared UTC-stable month formatting (used by timeTicks). NO formatDate — that
// formats in LOCAL tz (see plan Discovery), which would shift a midnight-UTC boundary's day.
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;
function fmtMonth(ts: number): string {
  return MONTHS[new Date(ts).getUTCMonth()];
}
function fmtMonthDay(ts: number): string {
  const d = new Date(ts);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

// Map a UTC-ms timestamp to a plot x-pixel under the time scale.
// Linear: x = ((ts - t0) / (t1 - t0)) * innerWidth, clamped to [0, innerWidth].
// t1 <= t0 guard: returns innerWidth (degenerate single-instant / inverted domain).
export function pxOf(
  ts: number,
  t0: number,
  t1: number,
  innerWidth: number
): number {
  if (t1 <= t0) return innerWidth;
  const frac = (ts - t0) / (t1 - t0);
  return Math.max(0, Math.min(1, frac)) * innerWidth;
}

// Pick which point indices RENDER a pill, so adjacent pill CENTERS are >= MIN_GAP px apart on the
// TIME scale. Deterministic, pure, sorted-ascending, deduped. Every returned index is rendered
// (pill by construction — no bare dots). MIN_GAP = PITCH (77), same constant as v2.
// Priority when space is scarce:  last  >  first  >  first-grade-crossing  >  greedy fill.
//   1. Always keep last (n-1) and first (0).
//   2. If a first grade-crossing index cx>0 exists AND clears MIN_GAP from BOTH endpoints in px,
//      keep it. Dropped FIRST on any conflict (H1 precedent).
//   3. Greedy FILL, farthest-point-first: repeatedly insert the candidate whose pixel distance to
//      its NEAREST already-kept neighbor is MAXIMAL, provided that distance >= MIN_GAP. Stop when
//      no candidate clears MIN_GAP. (Index tiebreak: smaller index wins on equal distance.)
// Degenerate: if pxOf(last) - pxOf(first) < MIN_GAP (tiny width / collapsed domain), keep BOTH
//   first and last anyway (cap 2 — mirrors v2 max(2, ...)). n<=2 returns all.
export function pickPillIndices(
  points: SeriesPoint[],
  t0: number,
  t1: number,
  innerWidth: number
): number[] {
  const n = points.length;
  if (n <= 2) return Array.from({ length: n }, (_, i) => i);

  const px = points.map(p => pxOf(p.ts, t0, t1, innerWidth));
  const keep = new Set<number>([0, n - 1]); // last + first always (cap-2 degenerate safe)

  // first grade-crossing
  let cx = 0;
  const g0 = gradeFromScore(points[0].score);
  for (let i = 1; i < n; i++) {
    if (gradeFromScore(points[i].score) !== g0) {
      cx = i;
      break;
    }
  }
  // keep crossing only if it clears MIN_GAP from BOTH endpoints (else sacrifice — H1)
  if (cx > 0 && px[cx] - px[0] >= PITCH && px[n - 1] - px[cx] >= PITCH) {
    keep.add(cx);
  }

  // greedy farthest-point-first fill
  for (;;) {
    let best = -1;
    let bestDist = -1;
    for (let i = 1; i < n - 1; i++) {
      if (keep.has(i)) continue;
      // nearest kept neighbor distance in px
      let nearest = Infinity;
      for (const k of keep)
        nearest = Math.min(nearest, Math.abs(px[i] - px[k]));
      // tiebreak: '>' (not '>=') keeps the FIRST (smaller) index on equal distance → deterministic
      if (nearest >= PITCH && nearest > bestDist) {
        bestDist = nearest;
        best = i;
      }
    }
    if (best < 0) break;
    keep.add(best);
  }
  return [...keep].sort((a, b) => a - b);
}

export interface TimeTick {
  ts: number;
  label: string;
}

// Generate one tick PER sampled point (v3 — ticks sit AT the grid targets, not on calendar
// boundaries), labelled fmtMonthDay for R1/R3 and fmtMonth for R12. Thinned to >= TICK_MIN_GAP
// (70px) center spacing for narrow widths, but the FIRST and LAST points are always kept (the
// window-edge anchors) — middles that would crowd the last point are dropped, not the last itself.
export function timeTicks(
  points: SeriesPoint[],
  t0: number,
  t1: number,
  innerWidth: number,
  win: WindowId
): TimeTick[] {
  const fmt = win === 'R12' ? fmtMonth : fmtMonthDay;
  const n = points.length;
  if (n === 0) return [];
  const tick = (p: SeriesPoint): TimeTick => ({ ts: p.ts, label: fmt(p.ts) });
  if (n <= 2) return points.map(tick); // first+last (or single) — never thinned

  const px = points.map(p => pxOf(p.ts, t0, t1, innerWidth));
  const lastX = px[n - 1];
  const out: TimeTick[] = [tick(points[0])]; // first always kept
  let keptX = px[0];
  for (let i = 1; i < n - 1; i++) {
    // keep this middle only if it clears MIN_GAP from the last kept AND from the (always-kept) last
    if (px[i] - keptX >= TICK_MIN_GAP && lastX - px[i] >= TICK_MIN_GAP) {
      out.push(tick(points[i]));
      keptX = px[i];
    }
  }
  out.push(tick(points[n - 1])); // last always kept
  return out;
}

// Single coverage predicate (DRY): true when there IS data AND the OLDEST sampled point lands on the
// window start (t0 = now − WINDOW_DAYS). Because sampleGrid emits points ON their target day, a
// populated oldest target sits EXACTLY at t0 (points[0].ts === t0); an omitted oldest target leaves
// points[0] at a later target (> t0). So "full coverage" ⟺ the oldest target is populated. The
// sublabel ('Full coverage' vs 'Since {Mon D}') and the chart's "Tracking since" overlay BOTH derive
// from this, so the two can never disagree. Empty series → false. nowMs/win derive t0.
export function hasFullCoverage(
  points: SeriesPoint[],
  win: WindowId,
  nowMs: number
): boolean {
  if (points.length === 0) return false;
  const t0 = nowMs - WINDOW_DAYS[win] * 86_400_000;
  return points[0].ts <= t0;
}
